/**
 * MaintenancePlanSettingsModal — admin/manager-only per-deal overrides for the
 * Roof Maintenance Agreement PDF. Auto-computed values (from effective SF) are
 * shown as reference; leave an override blank/0 to keep the auto value.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";

const money = (v) =>
  v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FIELD_CLS = "w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm";
const LABEL_CLS = "block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1";

export default function MaintenancePlanSettingsModal({ dealId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [form, setForm] = useState({
    plan_type: "",
    annual: "",
    biannual: "",
    hours: "",
    months: "",
  });

  useEffect(() => {
    if (!open || !dealId) return;
    setLoading(true);
    api
      .get(`/deals/${dealId}/maintenance-agreement/defaults`)
      .then((r) => {
        const d = r.data || {};
        setDefaults(d);
        setForm({
          plan_type: d.maintenance_plan_type || "",
          annual: d.maintenance_annual_fee_override > 0 ? String(d.maintenance_annual_fee_override) : "",
          biannual: d.maintenance_biannual_fee_override > 0 ? String(d.maintenance_biannual_fee_override) : "",
          hours: d.maintenance_repair_hours_override > 0 ? String(d.maintenance_repair_hours_override) : "",
          months: d.maintenance_preferred_months || "",
        });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load plan defaults"))
      .finally(() => setLoading(false));
  }, [open, dealId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/deals/${dealId}/maintenance-agreement/settings`, {
        maintenance_plan_type: form.plan_type,
        maintenance_annual_fee_override: parseFloat(form.annual) || 0,
        maintenance_biannual_fee_override: parseFloat(form.biannual) || 0,
        maintenance_repair_hours_override: parseInt(form.hours, 10) || 0,
        maintenance_preferred_months: form.months,
      });
      toast.success("Maintenance plan settings saved");
      onOpenChange(false);
      if (onSaved) onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save plan settings");
    } finally {
      setSaving(false);
    }
  };

  const sqftLabel =
    defaults?.effective_sqft > 0 ? `${Math.round(defaults.effective_sqft).toLocaleString()} SF` : "not set";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ma-settings-modal">
        <DialogHeader>
          <DialogTitle>Maintenance Plan Settings</DialogTitle>
          <DialogDescription>
            Per-contract overrides for the Maintenance Agreement PDF. Leave a field blank to use the
            auto-computed value from the roof surface ({sqftLabel}).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500" data-testid="ma-settings-loading">
            Loading plan defaults…
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className={LABEL_CLS}>Plan Selection (pre-checks the box on the PDF)</label>
              <select
                value={form.plan_type}
                onChange={set("plan_type")}
                data-testid="ma-settings-plan-type"
                className={FIELD_CLS}
              >
                <option value="">Customer selects at signing (both blank)</option>
                <option value="annual">Annual Plan — 1 inspection / yr</option>
                <option value="biannual">Bi-Annual Plan — 2 inspections / yr</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLS}>Annual Fee Override</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.annual}
                  onChange={set("annual")}
                  placeholder={defaults?.computed_annual_fee ? `Auto: ${money(defaults.computed_annual_fee)}` : "Auto: —"}
                  data-testid="ma-settings-annual-fee"
                  className={FIELD_CLS}
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  Auto: {money(defaults?.computed_annual_fee)} (size-tiered: $0.06/SF ≤50k · $0.05 &gt;50k · $0.042 &gt;100k · min $1,495)
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>Bi-Annual Fee Override</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.biannual}
                  onChange={set("biannual")}
                  placeholder={defaults?.computed_biannual_fee ? `Auto: ${money(defaults.computed_biannual_fee)}` : "Auto: —"}
                  data-testid="ma-settings-biannual-fee"
                  className={FIELD_CLS}
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  Auto: {money(defaults?.computed_biannual_fee)} (size-tiered ×2: $0.058 ≤50k · $0.048 &gt;50k · $0.04 &gt;100k · min $2,850, paid in advance)
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLS}>Repair Hours Override</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.hours}
                  onChange={set("hours")}
                  placeholder={defaults?.computed_repair_hours ? `Auto: ${defaults.computed_repair_hours} hrs` : "Auto: —"}
                  data-testid="ma-settings-repair-hours"
                  className={FIELD_CLS}
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  Auto: {defaults?.computed_repair_hours ?? "—"} hrs (size-tiered: 4 ≤25k · 6 ≤75k · 8 ≤150k · 10 ≤500k · 12 over)
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>Preferred Inspection Month(s)</label>
                <input
                  value={form.months}
                  onChange={set("months")}
                  placeholder="e.g. March / September"
                  data-testid="ma-settings-months"
                  className={FIELD_CLS}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="ma-settings-cancel"
            className="h-10 px-4 text-xs font-bold uppercase tracking-wider rounded-sm text-zinc-700 hover:bg-zinc-100"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="ma-settings-save"
            className="h-10 px-4 text-xs font-bold uppercase tracking-wider rounded-sm bg-teal-700 hover:bg-teal-800 text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
